// ============================================================
// src/theme.ts  —  Mera Lunch Box brand colors
// ============================================================
//  #535c6b  →  brand-500   dark slate   (logo card bg)
//  #fdec29  →  brand-yellow             ("MERA" gold)
//  #a1ca41  →  brand-green              ("LUNCH BOX" green)
//  #868a66  →  brand-muted              (olive-gray)
// ============================================================

export const colors = {
  brand: {
    slate:       '#535c6b',   // logo dark
    slateDark:   '#333A45',
    slateLight:  '#D8DBE0',
    yellow:      '#fdec29',   // logo gold
    yellowHover: '#e8d620',
    yellowLight: '#FFFDE7',
    yellowDark:  '#B8A800',
    green:       '#a1ca41',   // logo green
    greenHover:  '#8BB535',
    greenLight:  '#F2F8E4',
    greenDark:   '#6A8F22',
    muted:       '#868a66',   // olive-gray
    mutedLight:  '#F0F0E8',
    // compat aliases
    primary:     '#535c6b',
    primaryHover:'#333A45',
    secondary:   '#fdec29',
    accent:      '#fdec29',
    accentHover: '#e8d620',
    accentLight: '#FFFDE7',
    gradient:    'linear-gradient(135deg, #535c6b 0%, #333A45 100%)',
    gradientGold:'linear-gradient(135deg, #fdec29 0%, #e8d620 100%)',
  },
  red: {
    50: '#fef2f2', 100: '#fee2e2', 200: '#fecaca',
    500: '#ef4444', 600: '#dc2626', 700: '#b91c1c', 800: '#991b1b',
  },
  cyan:   { 50: '#ecfeff', 200: '#a5f3fc', 600: '#0891b2' },
  purple: { 50: '#faf5ff', 200: '#e9d5ff', 600: '#9333ea' },
  emerald:{ 400: '#34d399', 500: '#10b981' },
  orange: { 50: '#fff7ed', 200: '#fed7aa', 600: '#ea580c' },
  green:  { 50: '#f0fdf4', 100: '#dcfce7', 200: '#bbf7d0', 600: '#16a34a', 800: '#166534' },
  yellow: { 50: '#fefce8', 200: '#fef08a', 600: '#ca8a04' },
  gray: {
    50: '#f9fafb', 100: '#f3f4f6', 200: '#e5e7eb',
    300: '#d1d5db', 400: '#9ca3af', 500: '#6b7280',
    600: '#4b5563', 700: '#374151', 800: '#1f2937', 900: '#111827',
  },
  white: '#ffffff',
  black: '#000000',
} as const;

export const fontSize = {
  xs: 'text-xs', sm: 'text-sm', md: 'text-md', base: 'text-base',
  lg: 'text-lg', xl: 'text-xl', '2xl': 'text-2xl',
  '3xl': 'text-3xl', '4xl': 'text-4xl', '5xl': 'text-5xl',
} as const;

export const fontWeight = {
  normal: 'font-normal', medium: 'font-medium', semibold: 'font-semibold',
  bold: 'font-bold', extrabold: 'font-extrabold',
} as const;

export const textColor = {
  primary:      'text-brand-500',
  primaryHover: 'text-brand-700',
  accent:       'text-yellow-700',
  danger:       'text-red-600',
  dangerSoft:   'text-red-800',
  error:        'text-red-500',
  success:      'text-green-600',
  successSoft:  'text-green-800',
  warning:      'text-yellow-600',
  info:         'text-cyan-600',
  purple:       'text-purple-600',
  white:        'text-white',
  heading:      'text-gray-900',
  body:         'text-gray-800',
  secondary:    'text-gray-700',
  muted:        'text-gray-600',
  subtle:       'text-gray-500',
  icon:         'text-gray-400',
  iconBlue:     'text-brand-400',
  emerald:      'text-emerald-400',
  orange:       'text-orange-600',
  black:        'text-black',
} as const;

export const bgColor = {
  primary:       'bg-brand-500',
  primaryLight:  'bg-brand-50',
  primaryMedium: 'bg-brand-100',
  accent:        'bg-mlb-gold',
  accentLight:   'bg-yellow-50',
  danger:        'bg-red-600',
  dangerLight:   'bg-red-50',
  dangerMedium:  'bg-red-100',
  successLight:  'bg-green-50',
  successMedium: 'bg-green-100',
  warningLight:  'bg-yellow-50',
  infoLight:     'bg-cyan-50',
  purpleLight:   'bg-purple-50',
  white:         'bg-white',
  gray50:        'bg-gray-50',
  gray100:       'bg-gray-100',
  gray200:       'bg-gray-200',
  gray300:       'bg-gray-300',
} as const;

export const borderColor = {
  primary:  'border-brand-200',
  accent:   'border-mlb-gold',
  danger:   'border-red-200',
  error:    'border-red-500',
  success:  'border-green-200',
  warning:  'border-yellow-200',
  info:     'border-cyan-200',
  purple:   'border-purple-200',
  gray100:  'border-gray-100',
  gray200:  'border-gray-200',
  gray300:  'border-gray-300',
  orange:   'border-orange-200',
} as const;

export const hoverColor = {
  primaryBg:    'hover:bg-brand-700',
  primaryLight: 'hover:bg-brand-50',
  primaryText:  'hover:text-brand-700',
  dangerBg:     'hover:bg-red-700',
  dangerLight:  'hover:bg-red-50',
  gray50:       'hover:bg-gray-50',
  gray100:      'hover:bg-gray-100',
  gray300:      'hover:bg-gray-300',
  orangeText:   'hover:text-orange-600',
  blackText:    'hover:text-black',
} as const;

export const focusStyle = {
  primary:       'focus:ring-2 focus:ring-brand-400',
  primaryOffset: 'focus:ring-2 focus:ring-brand-400 focus:ring-offset-2',
} as const;

export const chartColors = {
  revenue:       colors.brand.primary,
  subscriptions: colors.emerald[500],
  axis:          colors.gray[500],
  grid:          colors.gray[200],
  tooltipBg:     colors.white,
  tooltipBorder: colors.gray[200],
  tooltipLabel:  colors.gray[900],
} as const;

export const toggleColors = {
  active:   colors.brand.primary,
  inactive: colors.gray[300],
} as const;

export const statCardVariants = {
  card: {
    primary: 'bg-brand-50 border-brand-200',
    success: 'bg-green-50 border-green-200',
    warning: 'bg-yellow-50 border-yellow-200',
    info:    'bg-cyan-50 border-cyan-200',
    danger:  'bg-red-50 border-red-200',
    purple:  'bg-purple-50 border-purple-200',
  },
  icon: {
    primary: 'text-brand-500',
    success: 'text-green-600',
    warning: 'text-yellow-600',
    info:    'text-cyan-600',
    danger:  'text-red-600',
    purple:  'text-purple-600',
  },
} as const;

export const buttonVariants = {
  primary:   'bg-brand-500 text-white hover:bg-brand-700',
  secondary: 'border-2 border-brand-500 text-brand-500 hover:bg-brand-50',
  accent:    'bg-mlb-gold text-gray-900 font-semibold hover:bg-mlb-gold/80',
  danger:    'bg-red-600 text-white hover:bg-red-700',
} as const;

export const buttonSizes = {
  sm: `h-8 px-3 ${fontSize.sm}`,
  md: `h-10 px-4 ${fontSize.sm}`,
  lg: `h-12 px-6 ${fontSize.base}`,
} as const;

export const statusBadge = {
  active:   'bg-green-100 text-green-800',
  inactive: 'bg-red-100 text-red-800',
} as const;
